import React, { useMemo, useRef } from "react";
import FeedbackMarker, { DIAMOND_D, DIAMOND_STATES, MARKER_STYLE } from "./FeedbackMarker";
import { EDGE } from "./edgeTokens";
import { measureText, wrapText } from "./measureText";

// Reusable Edge "tap the location" surface. It knows nothing about candles:
// the caller draws the chart through the `children` render prop (given the
// scales) and passes zones, markers and destinations in DATA coordinates.
// A later Read with a multi-candle chart reuses this as-is.
//
// Props
//   width, height        SVG viewBox size
//   plot                 { x0, x1, y0, y1 } drawing area inside the viewBox
//   xDomain, yDomain     data range mapped onto the plot
//   onTap(pt)            called with the tapped point in data units
//   disabled             ignore taps (e.g. after Submit)
//   zoneHints            rects to paint as the gold teaching zone (guided only)
//   markers              [{ key, x, y, state, label, side, sub? }]
//   miss                 { pt, key } soft neutral cue for a wrong guided tap
//   target               { point, title, sub, side } guided only: the gold target diamond
//                        plus a callout beside the candle with a dashed
//                        connector to it (reference board, section 1)
//   destinations         [{ id, label, point }] keyboard / screen-reader path
//   destinationsLabel    accessible name of the destination group
//   description          accessible description of the chart
export default function TapLocationSurface({
  width,
  height,
  plot,
  xDomain,
  yDomain,
  onTap,
  disabled,
  zoneHints = [],
  markers = [],
  miss,
  target,
  destinations = [],
  destinationsLabel,
  description,
  children,
}) {
  const svgRef = useRef(null);

  const scale = useMemo(() => {
    const xs = (plot.x1 - plot.x0) / (xDomain[1] - xDomain[0]);
    const ys = (plot.y1 - plot.y0) / (yDomain[1] - yDomain[0]);
    return {
      x: (d) => plot.x0 + (d - xDomain[0]) * xs,
      y: (p) => plot.y1 - (p - yDomain[0]) * ys,
      invert: (sx, sy) => ({ x: xDomain[0] + (sx - plot.x0) / xs, y: yDomain[0] + (plot.y1 - sy) / ys }),
    };
  }, [plot, xDomain, yDomain]);

  const handleClick = (e) => {
    if (disabled || !onTap || !svgRef.current) return;
    const svg = svgRef.current;
    const p = svg.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    const loc = p.matrixTransform(svg.getScreenCTM().inverse());
    onTap(scale.invert(loc.x, loc.y));
  };

  const placed = useMemo(() => placeMarkers(markers, scale), [markers, scale]);
  const chips = useMemo(() => layoutChips(placed, plot), [placed, plot]);

  return (
    <div role="group" aria-label={description}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className={`block w-full h-auto select-none ${disabled ? "" : "cursor-pointer"}`}
        style={{ touchAction: "manipulation" }}
        onClick={handleClick}
        aria-hidden="true"
      >
        <rect x={0} y={0} width={width} height={height} fill="#fff" />
        {zoneHints.map((r, i) => {
          const x = scale.x(r.x[0]);
          const y = scale.y(r.y[1]);
          return (
            <rect
              key={i}
              className="edge-hint"
              x={x}
              y={y}
              width={scale.x(r.x[1]) - x}
              height={scale.y(r.y[0]) - y}
              rx={6}
              fill={EDGE.gold}
              fillOpacity={0.2}
              stroke={EDGE.ink}
              strokeOpacity={0.3}
              strokeWidth={1.2}
              strokeDasharray="4 3"
            />
          );
        })}
        {children(scale)}
        {target && <TargetCallout target={target} scale={scale} plot={plot} />}
        {chips.map((c) => (
          <g key={`chip-${c.key}`} className="edge-fade-in" pointerEvents="none">
            <line
              x1={c.side === "left" ? c.x + c.w : c.x}
              y1={c.cy}
              x2={c.tx}
              y2={c.ty}
              stroke={MARKER_STYLE[c.state].chipStroke}
              strokeWidth={1.2}
              strokeDasharray="2 2"
            />
            <rect
              x={c.x}
              y={c.cy - c.h / 2}
              width={c.w}
              height={c.h}
              rx={6}
              fill="#fff"
              stroke={MARKER_STYLE[c.state].chipStroke}
              strokeWidth={1.4}
              strokeDasharray={MARKER_STYLE[c.state].dashed ? "3 2" : undefined}
            />
            <text
              x={c.x + c.w / 2}
              y={c.sub ? c.cy - 2.5 : c.cy + 3.8}
              textAnchor="middle"
              fontSize={11}
              fontWeight={600}
              fill={EDGE.ink}
            >
              {c.label}
            </text>
            {c.sub && (
              <text x={c.x + c.w / 2} y={c.cy + 9.5} textAnchor="middle" fontSize={9.5} fill={EDGE.neutral}>
                {c.sub}
              </text>
            )}
          </g>
        ))}
        {/* Draw order keeps every state visible when two share a point: the
            learner's hollow "Your answer" ring is drawn around, not under, the
            gold "Correct answer" diamond at the same spot. */}
        {placed
          .filter((m) => m.sx !== m.tx)
          .map((m) => (
            <line key={`tie-${m.key}`} x1={m.sx} y1={m.sy} x2={m.tx} y2={m.ty} stroke={EDGE.line} strokeWidth={1.2} />
          ))}
        {placed
          .map((m, i) => ({ m, i }))
          .sort((a, b) => DRAW_ORDER[a.m.state] - DRAW_ORDER[b.m.state])
          .map(({ m, i }) => (
            <FeedbackMarker key={m.key} cx={m.sx} cy={m.sy} state={m.state} side={m.side || "left"} delay={i * 140} />
          ))}
        {target && (
          <FeedbackMarker
            cx={scale.x(target.point.x)}
            cy={scale.y(target.point.y)}
            state="target"
            side={target.side || "left"}
          />
        )}
        {miss && <FeedbackMarker key={miss.key} cx={scale.x(miss.pt.x)} cy={scale.y(miss.pt.y)} state="miss" />}
      </svg>
      {destinations.length > 0 && (
        // Placement must not depend on pointing. Keyboard and screen-reader users
        // choose from named destinations instead of tapping in space; the group
        // stays visually hidden until something inside it takes focus.
        <div
          role="group"
          aria-label={destinationsLabel}
          className="sr-only focus-within:not-sr-only focus-within:flex flex-wrap gap-2 pt-3"
        >
          {destinations.map((d) => (
            <button
              key={d.id}
              type="button"
              disabled={disabled}
              onClick={() => onTap && onTap(d.point)}
              className="min-h-[44px] px-3 rounded-lg border border-gray-300 text-sm text-bodyInk bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]"
            >
              {d.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CALLOUT_PAD_X = 8;
const TITLE = { size: 11.5, weight: 600, lineH: 14 };
const SUB = { size: 10, weight: 400, lineH: 12.5 };

// Guided instruction drawn next to the candle, joined to the target dot by a
// dashed elbow connector, as in section 1 of the reference board. Sits in the
// left column, above the target where there is room, otherwise below it.
//
// The box is sized to its text (measured in the rendered font), never to a
// fixed width, and stops short of the candle edge so it never covers the
// candle; a line that would not fit wraps onto a second line.
function TargetCallout({ target, scale, plot }) {
  const tx = scale.x(target.point.x);
  const ty = scale.y(target.point.y);
  const x = plot.x0 + 4;
  // tx is where the target diamond touches the candle. The callout sits at
  // least 34 above or below the target, so it may run up to the candle edge
  // without meeting the diamond (which only spans the target's own row).
  const maxW = tx - 4 - x;
  const inner = maxW - CALLOUT_PAD_X * 2;
  const titleLines = wrapText(target.title, TITLE.size, TITLE.weight, inner);
  const subLines = target.sub ? wrapText(target.sub, SUB.size, SUB.weight, inner) : [];
  const widest = Math.max(
    ...titleLines.map((l) => measureText(l, TITLE.size, TITLE.weight)),
    ...subLines.map((l) => measureText(l, SUB.size, SUB.weight))
  );
  const w = Math.min(maxW, Math.ceil(widest) + CALLOUT_PAD_X * 2);
  const h = 9 + titleLines.length * TITLE.lineH + (subLines.length ? 2 + subLines.length * SUB.lineH : 0) + 7;

  const above = ty - 34 - h >= plot.y0;
  const y = above ? ty - 34 - h : Math.min(ty + 34, plot.y1 - h);
  const midX = x + w / 2;
  const edgeY = above ? y + h : y;
  const titleTop = y + 9 + TITLE.lineH - 3;
  const subTop = titleTop + (titleLines.length - 1) * TITLE.lineH + 2 + SUB.lineH;
  return (
    <g pointerEvents="none" className="edge-fade-in">
      <path
        // Ends at the target diamond's far point (the diamond extends toward
        // the callout, its other point touching the candle).
        d={`M${midX} ${edgeY} L${midX} ${ty} L${tx - 2 * DIAMOND_D} ${ty}`}
        fill="none"
        stroke={EDGE.gold}
        strokeWidth={1.6}
        strokeDasharray="4 3"
      />
      <rect x={x} y={y} width={w} height={h} rx={7} fill="#FFFDF6" stroke={EDGE.gold} strokeWidth={1.6} />
      {titleLines.map((l, i) => (
        <text
          key={`t${i}`}
          x={midX}
          y={titleTop + i * TITLE.lineH}
          textAnchor="middle"
          fontSize={TITLE.size}
          fontWeight={TITLE.weight}
          fill={EDGE.ink}
        >
          {l}
        </text>
      ))}
      {subLines.map((l, i) => (
        <text key={`s${i}`} x={midX} y={subTop + i * SUB.lineH} textAnchor="middle" fontSize={SUB.size} fill={EDGE.neutral}>
          {l}
        </text>
      ))}
    </g>
  );
}

const DRAW_ORDER = { neutral: 0, teach: 0, corrected: 1, incorrect: 2, correct: 3 };
// Chips are at least 84 wide (all six element names line up) and grow to fit
// their text, up to CHIP_MAX_W so a column never reaches the candle.
const CHIP_W = 84;
const CHIP_MAX_W = 100;
const CHIP_PAD_X = 7;
const CHIP_GAP = 4;

// Places each marker's label chip in a column beside the chart (left or right),
// at the marker's height, then pushes chips apart so none overlap. Markers that
// share a point (e.g. Open and Low on a no-lower-wick candle) get stacked chips
// with their own connector lines instead of one merged label.
// Where each marker is drawn. Markers sit exactly on their data point, with
// one exception the client called out: a teal check that a correction marker
// ("Your answer" ring or "Correct answer" diamond) lands on or near. The candle's
// key points are close together, so this is constant, not an edge case. The
// correction keeps the true point; the check steps aside toward its own label
// and a short faint tie line runs back to the true point, so neither is hidden
// and the check still reads as anchored.
//
// Diamonds that land on the same point (a "Your answer" and a "Correct answer"
// at the same body edge, or Open and Low on a no-lower-wick candle) are set
// side by side outward: the gold correction keeps the true point and leads,
// the next diamond starts at its far point.
const OVERLAP = 15;
const CHECK_STEP = 20;
const DIAMOND_ORDER = { teach: 0, corrected: 0, incorrect: 1 };
function placeMarkers(markers, scale) {
  const pts = markers.map((m) => ({ ...m, tx: scale.x(m.x), ty: scale.y(m.y) }));
  const shift = new Map();
  const diamonds = pts
    .filter((m) => DIAMOND_STATES.includes(m.state))
    .sort((a, b) => DIAMOND_ORDER[a.state] - DIAMOND_ORDER[b.state]);
  const taken = [];
  diamonds.forEach((m) => {
    const dir = (m.side || "left") === "left" ? -1 : 1;
    const same = taken.filter((o) => o.side === m.side && Math.abs(o.tx - m.tx) < 1 && Math.abs(o.ty - m.ty) < 4).length;
    shift.set(m, dir * same * 2 * DIAMOND_D);
    taken.push(m);
  });
  return pts.map((m) => {
    if (shift.has(m)) return { ...m, sx: m.tx + shift.get(m), sy: m.ty };
    let sx = m.tx;
    if (m.state === "correct") {
      const hit = pts.some(
        (o) =>
          o !== m &&
          (o.state === "incorrect" || o.state === "corrected") &&
          Math.hypot(o.tx - m.tx, o.ty - m.ty) < OVERLAP
      );
      if (hit) sx = m.tx + ((m.side || "left") === "left" ? -CHECK_STEP : CHECK_STEP);
    }
    return { ...m, sx, sy: m.ty };
  });
}

function layoutChips(markers, plot) {
  const out = [];
  ["left", "right"].forEach((side) => {
    const col = markers
      .filter((m) => m.label && (m.side || "left") === side)
      .map((m) => {
        // A marker may set its own sublabel ("Top of body"), or null for none;
        // otherwise the state's default ("Correct", "Your answer") is used.
        const sub = "sub" in m ? m.sub : MARKER_STYLE[m.state] && MARKER_STYLE[m.state].sublabel;
        return {
          key: m.key,
          state: m.state,
          label: m.label,
          sub,
          side,
          w: Math.min(
            CHIP_MAX_W,
            Math.max(
              CHIP_W,
              Math.ceil(Math.max(measureText(m.label, 11, 600), measureText(sub, 9.5))) + CHIP_PAD_X * 2
            )
          ),
          h: sub ? 30 : 22,
          // Chips point at where the marker is drawn: a diamond's far point.
          tx: DIAMOND_STATES.includes(m.state) ? m.sx + (side === "left" ? -1 : 1) * 2 * DIAMOND_D : m.sx,
          ty: m.sy,
        };
      })
      .sort((a, b) => a.ty - b.ty);
    let cursor = plot.y0;
    col.forEach((c) => {
      c.cy = Math.max(c.ty, cursor + c.h / 2);
      cursor = c.cy + c.h / 2 + CHIP_GAP;
    });
    // If the stack ran past the bottom, slide it back up.
    const overflow = cursor - CHIP_GAP - plot.y1;
    if (overflow > 0) col.forEach((c) => (c.cy -= overflow));
    col.forEach((c) => {
      c.x = side === "left" ? plot.x0 + 4 : plot.x1 - c.w - 4;
      out.push(c);
    });
  });
  return out;
}
